import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

function MermaidDiagramModal({open, onClose, svgHtml}) {
  const [zoom, setZoom] = React.useState(75);
  const [initialZoom, setInitialZoom] = React.useState(75);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const contentRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Set default zoom when modal opens
  React.useEffect(() => {
    if (!open) return;
    
    // Default zoom to 75%
    setInitialZoom(75);
    setZoom(75);
    setPan({ x: 0, y: 0 });
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      // Keyboard shortcuts for zoom
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setZoom(z => Math.min(500, z + 25));
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        setZoom(z => Math.max(25, z - 25));
      }
      if (e.key === '0') {
        e.preventDefault();
        setZoom(initialZoom);
        setPan({ x: 0, y: 0 });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, initialZoom]);

  // Mouse wheel zoom
  React.useEffect(() => {
    if (!open || !contentRef.current) return;
    
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -25 : 25;
        setZoom(z => Math.max(25, Math.min(500, z + delta)));
      }
    };
    
    const content = contentRef.current;
    content.addEventListener('wheel', handleWheel, { passive: false });
    return () => content.removeEventListener('wheel', handleWheel);
  }, [open]);

  // Pan/drag functionality
  const handleMouseDown = (e) => {
    if (zoom > initialZoom) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > initialZoom) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (!open) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [open, isDragging, dragStart, pan]);

  // Download diagram as PNG
  const handleDownload = async () => {
    try {
      // Create a temporary container
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = svgHtml;
      document.body.appendChild(tempDiv);

      const svgElement = tempDiv.querySelector('svg');
      if (!svgElement) {
        throw new Error('SVG element not found');
      }

      // Get SVG dimensions
      const bbox = svgElement.getBBox();
      const width = bbox.width || svgElement.width.baseVal.value || 800;
      const height = bbox.height || svgElement.height.baseVal.value || 600;

      // Set proper dimensions
      svgElement.setAttribute('width', width);
      svgElement.setAttribute('height', height);

      // Serialize SVG to string
      const svgData = new XMLSerializer().serializeToString(svgElement);
      
      // Convert SVG to data URL instead of blob URL (CSP compliant)
      const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);

      // Create canvas for PNG conversion
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = 2; // Higher resolution
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');
        
        // Set white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw SVG
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to data URL and download PNG
        const pngDataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `mermaid-diagram-${Date.now()}.png`;
        link.href = pngDataUrl;
        link.click();
        document.body.removeChild(tempDiv);
      };
      
      img.onerror = (error) => {
        console.error('Error loading SVG:', error);
        document.body.removeChild(tempDiv);
        alert('Failed to convert diagram. Please try SVG download instead.');
      };
      
      img.src = svgDataUrl;
    } catch (error) {
      console.error('Error downloading diagram:', error);
      alert('Failed to download diagram. Please try again.');
    }
  };

  // Download as SVG
  const handleDownloadSvg = () => {
    try {
      const blob = new Blob([svgHtml], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `mermaid-diagram-${Date.now()}.svg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading SVG:', error);
      alert('Failed to download SVG. Please try again.');
    }
  };

  if (!open) return null;

  // Render modal in a portal attached to document.body
  return ReactDOM.createPortal(
    <div className="mermaid-modal" onClick={onClose} ref={containerRef}>
      <button aria-label="Close" className="mermaid-modal-close" onClick={onClose}>{"\u00d7"}</button>
      <div 
        className="mermaid-modal-content" 
        onClick={e => e.stopPropagation()} 
        ref={contentRef}
      >
        <div 
          onMouseDown={handleMouseDown}
          style={{
            transform: `scale(${zoom / 100}) translate(${zoom > initialZoom ? pan.x : 0}px, ${zoom > initialZoom ? pan.y : 0}px)`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            cursor: zoom > initialZoom ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          dangerouslySetInnerHTML={{__html: svgHtml}} 
        />
      </div>
      <div className="mermaid-modal-zoom-controls">
        <button 
          className="mermaid-modal-zoom-btn" 
          disabled={zoom <= 25} 
          onClick={e => {e.stopPropagation(); setZoom(z => Math.max(25, z - 25));}}
        >
          {'Zoom Out'}
        </button>
        <button 
          className="mermaid-modal-zoom-btn mermaid-modal-reset-btn" 
          disabled={zoom === initialZoom}
          onClick={e => {e.stopPropagation(); setZoom(initialZoom); setPan({ x: 0, y: 0 });}}
        >
          {`Reset (${Math.round(initialZoom)}%)`}
        </button>
        <span className="mermaid-modal-zoom-span">{'Zoom: '}{zoom}{'%'}</span>
        <button 
          className="mermaid-modal-zoom-btn" 
          disabled={zoom >= 500}
          onClick={e => {e.stopPropagation(); setZoom(z => Math.min(500, z + 25));}}
        >
          {'Zoom In'}
        </button>
        <button 
          className="mermaid-modal-download-btn" 
          onClick={e => {e.stopPropagation(); handleDownload();}}
          title="Download as PNG"
        >
          {'📥 PNG'}
        </button>
        <button 
          className="mermaid-modal-download-btn" 
          onClick={e => {e.stopPropagation(); handleDownloadSvg();}}
          title="Download as SVG"
        >
          {'📥 SVG'}
        </button>
      </div>
      <div className="mermaid-modal-help">
        {'Use Ctrl+Scroll to zoom • +/- keys to zoom • 0 to reset • ESC to close'}
      </div>
    </div>,
    document.body
  );
}

MermaidDiagramModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  svgHtml: PropTypes.string.isRequired,
};

export default MermaidDiagramModal;
